import { DEFAULT_CAPTION_LINES, type CaptionLines } from '../captions/placement';
import type { CaptionSettings } from '../captions/types';
import type { Size } from '../geometry/framing';
import type { TimeRange } from '../time/ranges';
import { telemetryAt, type Telemetry, type TelemetryReading } from './telemetry';
import { inRanges } from './zones';

export type TelemetryStyleId = 'dial' | 'minimal';
export const TELEMETRY_STYLES: readonly TelemetryStyleId[] = ['dial', 'minimal'];

export type OverlayCorner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
export const OVERLAY_CORNERS: readonly OverlayCorner[] = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];

export type TelemetryField = 'speed' | 'altitude' | 'lean' | 'gforce';
export const TELEMETRY_FIELDS: readonly TelemetryField[] = ['speed', 'altitude', 'lean', 'gforce'];

export type UnitSystem = 'metric' | 'imperial';

export interface TelemetrySettings {
  /** Burn the gauge into the video (when the source has GPS data). */
  readonly enabled: boolean;
  readonly style: TelemetryStyleId;
  readonly corner: OverlayCorner;
  /** 0..1 relative size. */
  readonly size: number;
  /** Shown values, in display order. */
  readonly fields: readonly TelemetryField[];
  readonly units: UnitSystem;
}

export const DEFAULT_TELEMETRY_SETTINGS: TelemetrySettings = {
  enabled: false,
  style: 'dial',
  corner: 'bottom-left',
  size: 0.5,
  fields: ['speed', 'altitude'],
  units: 'metric',
};

/** Countries that drive in miles per hour (and mostly measure height in feet). */
const IMPERIAL_REGIONS = new Set(['US', 'GB', 'LR', 'MM']);

/** Best guess from a BCP 47 locale such as `en-US`; metric unless the region says otherwise. */
export function defaultUnitSystem(locale: string | undefined): UnitSystem {
  const region = locale?.split(/[-_]/)[1]?.toUpperCase();
  return region && IMPERIAL_REGIONS.has(region) ? 'imperial' : 'metric';
}

export const UNIT_LABELS: Readonly<Record<UnitSystem, { readonly speed: string; readonly altitude: string; readonly distance: string }>> = {
  metric: { speed: 'km/h', altitude: 'm', distance: 'km' },
  imperial: { speed: 'mph', altitude: 'ft', distance: 'mi' },
};

/** m/s → km/h or mph. */
export const toSpeedUnit = (metresPerSecond: number, units: UnitSystem) => metresPerSecond * (units === 'metric' ? 3.6 : 2.2369363);
/** m → m or ft. */
export const toAltitudeUnit = (metres: number, units: UnitSystem) => (units === 'metric' ? metres : metres * 3.2808399);
/** m → km or mi. */
export const toDistanceUnit = (metres: number, units: UnitSystem) => metres / (units === 'metric' ? 1000 : 1609.344);

/**
 * Full scale of the speed dial: the ride's top speed rounded up to a round
 * number, so the arc uses its whole sweep without ever pinning.
 */
export function dialScale(maxSpeed: number, units: UnitSystem): number {
  const top = toSpeedUnit(maxSpeed, units);
  const step = top > 150 ? 50 : 20;
  return Math.max(40, Math.ceil((top * 1.05) / step) * step);
}

/** Everything the renderer needs for one frame. */
export interface TelemetryFrame {
  readonly reading: TelemetryReading;
  readonly settings: TelemetrySettings;
  readonly scale: number;
  /** Where burned-in captions sit, so the gauge can stay clear of them. */
  readonly captionZone: CaptionSettings['position'] | null;
  /** The lines the caption positions sit on (higher for a place that covers the bottom). */
  readonly captionLines: CaptionLines;
}

/** The overlay for a source time, or null when it is off or there is no data then. */
export function telemetryFrameAt(telemetry: Telemetry | null, settings: TelemetrySettings, captions: CaptionSettings, time: number): TelemetryFrame | null {
  if (!telemetry || !settings.enabled || settings.fields.length === 0) return null;
  const reading = telemetryAt(telemetry, time);
  if (!reading) return null;
  return {
    reading,
    settings,
    scale: dialScale(telemetry.stats.maxSpeed, settings.units),
    captionZone: captions.enabled && captions.cues.length > 0 ? captions.position : null,
    captionLines: captions.lines ?? DEFAULT_CAPTION_LINES,
  };
}

/**
 * What a frame shows on top of the footage, shared by preview and export:
 * near a private place the picture is veiled and the gauge stays off.
 */
export function rideLayersAt(
  input: { readonly veil: readonly TimeRange[]; readonly telemetry: Telemetry | null; readonly settings: TelemetrySettings; readonly captions: CaptionSettings },
  time: number,
  /** The moment of the ride data's own video showing at `time` (an edit can join other videos); null shows no gauge. */
  telemetryTime: number | null = time,
): { readonly veiled: boolean; readonly telemetry: TelemetryFrame | null } {
  const veiled = inRanges(input.veil, time);
  return { veiled, telemetry: veiled || telemetryTime === null ? null : telemetryFrameAt(input.telemetry, input.settings, input.captions, telemetryTime) };
}

export interface ClusterPlacement {
  /** Top-left corner of the cluster box, in output pixels. */
  readonly x: number;
  readonly y: number;
  /** Base unit every part of the gauge is sized from (the dial's diameter). */
  readonly unit: number;
  /** Whether the cluster grows to the right (left corners) or to the left. */
  readonly fromLeft: boolean;
}

/**
 * Where the gauge goes. Margins scale with the frame; vertical videos keep
 * clear of the platform UI at the bottom, and the gauge steps above (or
 * below) captions placed in the same band.
 */
export function placeCluster(
  frame: Size,
  corner: OverlayCorner,
  size: number,
  box: Size,
  captionZone: TelemetryFrame['captionZone'],
  captionLines: CaptionLines = DEFAULT_CAPTION_LINES,
): ClusterPlacement {
  const short = Math.min(frame.width, frame.height);
  const unit = short * (0.17 + 0.14 * Math.min(1, Math.max(0, size)));
  const margin = short * 0.05;
  const vertical = frame.height > frame.width * 1.2;
  const top = corner.startsWith('top');
  const fromLeft = corner.endsWith('left');
  // Captions end on their bottom line and start on their top one; leave room for two lines.
  const captionBand = short * 0.2;
  const bottomInset = Math.max(vertical ? frame.height * 0.14 : margin, captionZone === 'bottom' ? frame.height * (1 - captionLines.bottom) + captionBand : 0);
  const topInset = Math.max(vertical ? frame.height * 0.08 : margin, captionZone === 'top' ? frame.height * captionLines.top + captionBand : 0);
  const width = box.width * unit;
  const height = box.height * unit;
  return {
    x: fromLeft ? margin : frame.width - margin - width,
    y: top ? topInset : frame.height - bottomInset - height,
    unit,
    fromLeft,
  };
}
